/**
 * Reusable validated input field components
 * Provides consistent validation and styling across all forms
 */

import React, { useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { validateEmail, validatePhone, validatePostcode } from '../../utils/validation';

// Common styling for all validated fields - matches GP Phone styling
const commonSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
  },
  '& .MuiInputBase-input': { 
    color: 'white',
    padding: '12px 14px',
  },
  '& .MuiInputLabel-root': { 
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': { color: '#6366f1' }
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
  '& .MuiFormHelperText-root': { 
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.75rem',
    marginLeft: 0,
    '&.Mui-error': { color: '#f44336' }
  }
};

interface ValidatedFieldProps extends Omit<TextFieldProps, 'error' | 'helperText' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  validateOnBlur?: boolean;
  externalError?: string;
}

/**
 * Email field with built-in validation
 */
export const EmailField: React.FC<ValidatedFieldProps> = ({
  value,
  onChange,
  validateOnBlur = true,
  externalError,
  required = false,
  ...props
}) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string>('');

  const handleBlur = () => {
    setTouched(true);
    if (validateOnBlur && value) {
      const result = validateEmail(value);
      setInternalError(result.error || '');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (touched) {
      const result = validateEmail(e.target.value);
      setInternalError(result.error || '');
    }
  };

  const error = externalError || (touched ? internalError : '');

  return (
    <TextField
      {...props}
      type="email"
      label={props.label || 'Email Address'}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!error}
      helperText={error}
      required={required}
      fullWidth
      className="theme-input"
      sx={commonSx}
    />
  );
};

/**
 * Phone field with built-in UK format validation
 */
export const PhoneField: React.FC<ValidatedFieldProps> = ({
  value,
  onChange,
  validateOnBlur = true,
  externalError,
  required = false,
  ...props
}) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string>('');

  const handleBlur = () => {
    setTouched(true);
    if (validateOnBlur && value) {
      const result = validatePhone(value);
      setInternalError(result.error || '');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (touched) {
      const result = validatePhone(e.target.value);
      setInternalError(result.error || '');
    }
  };

  const error = externalError || (touched ? internalError : '');

  return (
    <TextField
      {...props}
      type="tel"
      label={props.label || 'Phone Number'}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!error}
      helperText={error || 'UK format: 07xxx xxxxxx or 01xxx xxxxxx'}
      required={required}
      fullWidth
      className="theme-input"
      sx={commonSx}
    />
  );
};

/**
 * Postcode field with built-in UK format validation
 */
export const PostcodeField: React.FC<ValidatedFieldProps> = ({
  value,
  onChange,
  validateOnBlur = true,
  externalError,
  required = false,
  ...props
}) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string>('');

  const handleBlur = () => {
    setTouched(true);
    if (validateOnBlur && value) {
      const result = validatePostcode(value);
      setInternalError(result.error || '');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Auto-uppercase postcodes
    const uppercased = e.target.value.toUpperCase();
    onChange(uppercased);
    if (touched) {
      const result = validatePostcode(uppercased);
      setInternalError(result.error || '');
    }
  };

  const error = externalError || (touched ? internalError : '');

  return (
    <TextField
      {...props}
      type="text"
      label={props.label || 'Postcode'}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!error}
      helperText={error || 'UK format: SW1A 1AA'}
      required={required}
      fullWidth
      className="theme-input"
      sx={commonSx}
      inputProps={{
        ...props.inputProps,
        style: { textTransform: 'uppercase' }
      }}
    />
  );
};

/**
 * Generic validated text field
 */
export const ValidatedTextField: React.FC<ValidatedFieldProps & {
  validator?: (value: string) => { isValid: boolean; error?: string };
  helperText?: string;
}> = ({
  value,
  onChange,
  validator,
  validateOnBlur = true,
  externalError,
  required = false,
  helperText,
  ...props
}) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string>('');

  const handleBlur = () => {
    setTouched(true);
    if (validateOnBlur && value && validator) {
      const result = validator(value);
      setInternalError(result.error || '');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (touched && validator) {
      const result = validator(e.target.value);
      setInternalError(result.error || '');
    }
  };

  const error = externalError || (touched ? internalError : '');
  const displayHelperText = error || helperText || '';

  return (
    <TextField
      {...props}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!error}
      helperText={displayHelperText}
      required={required}
      fullWidth
      className="theme-input"
      sx={commonSx}
    />
  );
};

/**
 * Themed text field without validation - for general use
 * Maintains consistent styling with validated fields
 */
export const ThemedTextField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  label: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  helperText?: string;
}> = ({ 
  value, 
  onChange, 
  label, 
  type = 'text', 
  required = false, 
  disabled = false,
  multiline = false,
  rows,
  placeholder,
  helperText
}) => {
  return (
    <TextField
      fullWidth
      type={type}
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      placeholder={placeholder}
      helperText={helperText}
      className="theme-input"
      sx={commonSx}
    />
  );
};
